/** HTTP client for the AI form-authoring assistant backend.
 *
 * The AI service never writes to Supabase — it only proposes mutations on the
 * in-memory FormDefinition the editor holds in React state. The user reviews,
 * accepts, then saves via the normal draft-save flow.
 *
 * All AI traffic is namespaced under `/ai/...`. In dev, Vite proxies `/ai/*`
 * to the Flask backend on :7400 (see `vite.config.ts`) and strips the `/ai`
 * prefix. In prod, set `VITE_AI_ASSISTANT_URL` to the absolute backend URL.
 */

const BASE = (import.meta.env.VITE_AI_ASSISTANT_URL as string | undefined) || ''

export interface AssistantValidationIssue {
  path: string
  message: string
  schema_path: string
}

export interface AssistantToolTraceEntry {
  tool: string
  arguments: unknown
  summary?: string
  error?: string
  attempts: number
}

export interface AssistantUserSummary {
  headline: string
  paragraph: string
  bullets: string[]
  if_accepted: string
  warnings: string[]
  raw?: string | null
  error?: string | null
}

export interface AssistantChatResult {
  assistant_message: string
  updated_form: any
  patch: Array<{ op: string; path: string; value?: unknown; from?: string }>
  form_valid: boolean
  validation_report: { ok: boolean; issues: AssistantValidationIssue[] }
  tool_trace: AssistantToolTraceEntry[]
  steps: number
  stopped_reason: string
  user_summary?: AssistantUserSummary | null
}

export interface AssistantJobRun {
  id: string
  kind: string
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelling' | 'cancelled'
  progress: number
  stage: string
  message: string
  error: string | null
  scope: Record<string, any>
  result: AssistantChatResult | any
  started_at: string | null
  ended_at: string | null
  created_at: string
}

export interface AssistantChatHistoryTurn {
  role: 'user' | 'assistant'
  content: string
}

export interface AssistantChatRequest {
  form: unknown
  instruction: string
  locale?: 'fa' | 'en'
  context?: Record<string, unknown>
  history?: AssistantChatHistoryTurn[]
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  })
  const text = await res.text()
  const body = text ? safeJson(text) : null
  if (!res.ok) {
    const msg = (body && typeof body === 'object' && (body as any).error?.message) || res.statusText
    throw new Error(msg)
  }
  return body as T
}

function safeJson(t: string): unknown {
  try { return JSON.parse(t) } catch { return { raw: t } }
}

export const assistantApi = {
  chat: (req: AssistantChatRequest) =>
    jsonFetch<{ job_id: string }>('/ai/api/assistant/chat', {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  getJob: (id: string) => jsonFetch<AssistantJobRun>(`/ai/api/jobs/${id}`),
  cancelJob: (id: string) =>
    jsonFetch<AssistantJobRun>(`/ai/api/jobs/${id}/cancel`, { method: 'POST' }),
}

export async function pollAssistantJob(
  jobId: string,
  onUpdate: (j: AssistantJobRun) => void,
  intervalMs = 800,
): Promise<AssistantJobRun> {
  while (true) {
    const j = await assistantApi.getJob(jobId)
    onUpdate(j)
    if (j.status === 'succeeded' || j.status === 'failed' || j.status === 'cancelled') return j
    await new Promise((r) => setTimeout(r, intervalMs))
  }
}
